import { commonText, useCommonLanguage, commonNodes } from "../../i18n/commonPresentation";
/**
 * TagPicker -- Searchable multi-select tag picker with popover.
 *
 * Features:
 * - Click trigger to open a floating popover with fuzzy search
 * - Selected tags shown as removable pills in the trigger area
 * - Type to filter existing suggestions; press Enter to create new tags
 * - Keyboard navigation: arrow keys to browse, Enter to select/create, Escape to close
 * - Accessible: ARIA combobox pattern, focus management
 *
 * Inspired by Notion/Linear tag pickers.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/cn";
import { Icon } from "./Icon";
import { useAnchoredPosition } from "./useAnchoredPosition";

// ============================================================================
// Types
// ============================================================================

export type TagPickerProps = {
  /** Currently selected tags */
  value: string[];
  /** Called when tags change (add/remove) */
  onChange: (tags: string[]) => void;
  /** Optional list of suggested tags to show in the dropdown */
  suggestions?: string[];
  /** Placeholder text when no tags are selected */
  placeholder?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Label for accessibility */
  "aria-label"?: string;
  /** Additional class for the trigger container */
  className?: string;
  /** Maximum number of tags allowed (0 = unlimited) */
  maxTags?: number;
  /** Allow creating new tags not in suggestions */
  allowCreate?: boolean;
  /**
   * Popover panel width. Every value EXCEPT "trigger" is sized independently of
   * the trigger, so a picker of short tokens is not forced to the width of a
   * wide parent field:
   * - "trigger" (default): panel matches the trigger width (legacy behavior).
   * - "auto": panel hugs its content between a floor (200px) and a cap (360px / 90vw).
   * - "sm" | "md" | "lg": a fixed width from the defined scale (200 / 264 / 340px).
   * - number: an explicit width in px.
   * All fixed values are capped at 90vw so they never exceed a narrow viewport.
   */
  panelSize?: "trigger" | "auto" | "sm" | "md" | "lg" | number;
  /**
   * Number of columns the option list is laid out in (default 1 = legacy single
   * column). Use 2-3 for long lists so options wrap into a grid that scrolls,
   * instead of one very tall column. The panel widens to fit the columns, taking
   * precedence over `panelSize`'s width. Arrow-key traversal becomes grid-aware
   * (Left/Right within a row, Up/Down by a whole row). The search/create rows
   * always span the full panel width.
   */
  panelColumns?: 1 | 2 | 3;
  /** Additional class for the popover panel */
  panelClassName?: string;
};

/** Named popover widths for `panelSize` (px). */
const PANEL_SIZE_PX: Record<"sm" | "md" | "lg", number> = { sm: 200, md: 264, lg: 340 };

export type TagPickerRef = {
  focus: () => void;
};

// ============================================================================
// Fuzzy match helper
// ============================================================================

function fuzzyMatch(text: string, query: string): boolean {
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  let qi = 0;
  for (let i = 0; i < lower.length && qi < q.length; i++) {
    if (lower[i] === q[qi]) qi++;
  }
  return qi === q.length;
}

// ============================================================================
// Component
// ============================================================================

export const TagPicker = forwardRef<TagPickerRef, TagPickerProps>(
  (
    {
      value,
      onChange,
      suggestions = [],
      placeholder = commonText("selectTags"),
      disabled = false,
      "aria-label": ariaLabel,
      className,
      maxTags = 0,
      allowCreate = true,
      panelSize = "trigger",
      panelColumns = 1,
      panelClassName,
    },
    ref,
  ) => {
    useCommonLanguage();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [highlightIndex, setHighlightIndex] = useState(0);

    const triggerRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const anchored = useAnchoredPosition({ open, anchor: triggerRef, panelRef });

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
    }));

    // Build filtered options list
    const selectedSet = useMemo(() => new Set(value), [value]);

    const filteredOptions = useMemo(() => {
      // Combine suggestions with currently selected tags (in case selected tags aren't in suggestions)
      const allTags = new Set([...suggestions, ...value]);
      let items = Array.from(allTags).sort((a, b) => a.localeCompare(b));

      if (query.trim()) {
        items = items.filter((tag) => fuzzyMatch(tag, query.trim()));
      }

      return items;
    }, [suggestions, value, query]);

    // Check if query matches an existing option exactly
    const queryMatchesExisting = useMemo(
      () => filteredOptions.some((t) => t.toLowerCase() === query.trim().toLowerCase()),
      [filteredOptions, query],
    );

    // Can create a new tag from current query?
    const canCreate = allowCreate && query.trim().length > 0 && !queryMatchesExisting;

    // Total items in dropdown (filtered options + optional create row)
    const totalItems = filteredOptions.length + (canCreate ? 1 : 0);

    // Clamp highlight
    useEffect(() => {
      setHighlightIndex((prev) => Math.min(prev, Math.max(0, totalItems - 1)));
    }, [totalItems]);

    // Reset query and highlight when opening
    useEffect(() => {
      if (open) {
        setQuery("");
        setHighlightIndex(0);
        // Focus the input after a frame so the popover has rendered
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    }, [open]);

    // Click outside to close
    useEffect(() => {
      if (!open) return;
      const handler = (e: MouseEvent) => {
        const target = e.target as Node;
        if (panelRef.current?.contains(target)) return;
        if (triggerRef.current?.contains(target)) return;
        setOpen(false);
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    // Scroll highlighted item into view
    useEffect(() => {
      if (!open || !listRef.current) return;
      const item = listRef.current.children[highlightIndex] as HTMLElement | undefined;
      item?.scrollIntoView({ block: "nearest" });
    }, [highlightIndex, open]);

    // Toggle a tag
    const toggleTag = useCallback(
      (tag: string) => {
        if (selectedSet.has(tag)) {
          onChange(value.filter((t) => t !== tag));
        } else {
          if (maxTags > 0 && value.length >= maxTags) return;
          onChange([...value, tag]);
        }
        setQuery("");
        setHighlightIndex(0);
        inputRef.current?.focus();
      },
      [value, onChange, selectedSet, maxTags],
    );

    // Create a new tag
    const createTag = useCallback(
      (tag: string) => {
        const trimmed = tag.trim();
        if (!trimmed || selectedSet.has(trimmed)) return;
        if (maxTags > 0 && value.length >= maxTags) return;
        onChange([...value, trimmed]);
        setQuery("");
        setHighlightIndex(0);
        inputRef.current?.focus();
      },
      [value, onChange, selectedSet, maxTags],
    );

    // Remove a tag (from pills)
    const removeTag = useCallback(
      (tag: string) => {
        onChange(value.filter((t) => t !== tag));
      },
      [value, onChange],
    );

    // Keyboard handling
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Escape") {
          e.preventDefault();
          setOpen(false);
          triggerRef.current?.focus();
          return;
        }

        // In a multi-column grid, Up/Down move by a whole row (panelColumns) and
        // Left/Right step within the row; single-column reduces both to +/-1.
        const cols = panelColumns;

        if (e.key === "ArrowDown") {
          e.preventDefault();
          setHighlightIndex((prev) => Math.min(prev + cols, totalItems - 1));
          return;
        }

        if (e.key === "ArrowUp") {
          e.preventDefault();
          setHighlightIndex((prev) => Math.max(prev - cols, 0));
          return;
        }

        if (cols > 1 && e.key === "ArrowRight") {
          e.preventDefault();
          setHighlightIndex((prev) => Math.min(prev + 1, totalItems - 1));
          return;
        }

        if (cols > 1 && e.key === "ArrowLeft") {
          e.preventDefault();
          setHighlightIndex((prev) => Math.max(prev - 1, 0));
          return;
        }

        if (e.key === "Enter") {
          e.preventDefault();
          if (totalItems === 0) return;

          // If highlight is on the "Create" row
          if (canCreate && highlightIndex === filteredOptions.length) {
            createTag(query.trim());
            return;
          }

          // Otherwise toggle the highlighted option
          const option = filteredOptions[highlightIndex];
          if (option) toggleTag(option);
          return;
        }

        if (e.key === "Backspace" && query === "" && value.length > 0) {
          onChange(value.slice(0, -1));
        }
      },
      [totalItems, highlightIndex, filteredOptions, canCreate, query, toggleTag, createTag, value, onChange, panelColumns],
    );

    // Render the pill tags in the trigger
    const renderTrigger = () => (
      <div
        ref={triggerRef}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        tabIndex={disabled ? -1 : 0}
        onClick={() => {
          if (!disabled) setOpen(true);
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={cn(
          "flex flex-wrap items-center gap-1 rounded-md border border-border/50 bg-background px-2 py-1.5 min-h-[32px] cursor-pointer",
          "hover:border-border transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:border-primary/50",
          disabled && "opacity-50 cursor-not-allowed",
          open && "ring-2 ring-primary/40 border-primary/50",
          className,
        )}
      >
        {value.length === 0 && (
          <span className="text-xs text-muted-foreground">{placeholder}</span>
        )}
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-0.5 bg-primary/10 text-primary-text border border-primary/20 rounded px-1.5 py-0.5 text-[10px] 2xl:text-xs"
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(tag);
                }}
                className="ml-0.5 text-primary/60 hover:text-primary transition-colors focus-visible:outline-none"
                aria-label={commonText("removeTag", { tag })}
                tabIndex={-1}
              >
                <Icon icon="lucide:x" className="h-2.5 w-2.5" />
              </button>
            )}
          </span>
        ))}
        <Icon
          icon="lucide:chevron-down"
          className={cn(
            "h-3 w-3 text-muted-foreground/40 ml-auto shrink-0 transition-transform",
            open && "rotate-180",
          )}
        />
      </div>
    );

    // Render the dropdown panel
    const renderPanel = () => (
      <div
        ref={panelRef}
        role="listbox"
        aria-label={ariaLabel ?? commonText("tagPicker")}
        style={{
          ...anchored.style,
          ...(panelColumns > 1
            ? { width: `min(${panelColumns * 160}px, 90vw)` }
            : panelSize === "trigger"
              ? { width: triggerRef.current?.offsetWidth ?? 280 }
              : panelSize === "auto"
                ? {
                    width: "max-content",
                    minWidth: Math.min(triggerRef.current?.offsetWidth ?? 200, 200),
                    maxWidth: "min(360px, 90vw)",
                  }
                : {
                    width: `min(${typeof panelSize === "number" ? panelSize : PANEL_SIZE_PX[panelSize]}px, 90vw)`,
                  }),
          maxHeight: "min(50vh, 320px, var(--qb-available-h, 100vh))",
        }}
        className={cn(
          "z-[9999] flex flex-col overflow-hidden rounded-lg border border-border/60 bg-card/95 text-sm text-foreground shadow-soft outline-none",
          anchored.className,
          panelClassName,
        )}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-2 px-2.5 py-2 border-b border-border/30">
          <Icon icon="lucide:search" className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlightIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder={commonText("searchOrCreate")}
            className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground border-none outline-none p-0"
            aria-label={commonText("searchTags")}
            autoComplete="off"
            spellCheck={false}
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                inputRef.current?.focus();
              }}
              className="text-muted-foreground/80 hover:text-muted-foreground transition-colors"
              tabIndex={-1}
              aria-label={commonText("clearSearch")}
            >
              <Icon icon="lucide:x" className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Options list */}
        <div
          ref={listRef}
          className={cn(
            "overflow-y-auto qb-scroll flex-1 max-h-[240px]",
            panelColumns === 2 && "grid grid-cols-2 gap-0.5 p-1",
            panelColumns === 3 && "grid grid-cols-3 gap-0.5 p-1",
          )}
        >
          {filteredOptions.length === 0 && !canCreate && (
            <div
              className={cn(
                "px-3 py-4 text-center text-xs text-muted-foreground",
                panelColumns > 1 && "col-span-full",
              )}
            >
              {query ? commonText("noMatchingTags") : commonText("noTags")}
            </div>
          )}

          {filteredOptions.map((tag, idx) => {
            const isSelected = selectedSet.has(tag);
            const isHighlighted = idx === highlightIndex;

            return (
              <button
                key={tag}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => toggleTag(tag)}
                onMouseEnter={() => setHighlightIndex(idx)}
                className={cn(
                  "flex items-center gap-2 w-full px-2.5 py-1.5 text-left text-xs transition-colors",
                  panelColumns > 1 && "rounded",
                  isHighlighted ? "bg-primary/10" : "hover:bg-muted/30",
                  isSelected && "text-primary-text font-medium",
                )}
              >
                <span
                  className={cn(
                    "flex items-center justify-center h-3.5 w-3.5 rounded border shrink-0",
                    isSelected
                      ? "bg-primary border-primary text-primary-foreground"
                      : "border-border/60 bg-background",
                  )}
                >
                  {isSelected && <Icon icon="lucide:check" className="h-2.5 w-2.5" />}
                </span>
                <span className="truncate">{tag}</span>
              </button>
            );
          })}

          {/* Create new tag row */}
          {canCreate && (
            <button
              type="button"
              role="option"
              aria-selected={false}
              onClick={() => createTag(query.trim())}
              onMouseEnter={() => setHighlightIndex(filteredOptions.length)}
              className={cn(
                "flex items-center gap-2 w-full px-2.5 py-1.5 text-left text-xs transition-colors border-t border-border/20",
                panelColumns > 1 && "col-span-full",
                highlightIndex === filteredOptions.length ? "bg-primary/10" : "hover:bg-muted/30",
              )}
            >
              <Icon icon="lucide:plus" className="h-3.5 w-3.5 text-primary shrink-0" />
              <span className="text-primary-text">
                {commonNodes("createTag", { tag: <span key="tag" className="font-medium">{query.trim()}</span> })}
              </span>
            </button>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-2.5 py-1.5 border-t border-border/20 text-[10px] text-muted-foreground flex items-center gap-3">
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 rounded bg-muted/30 font-mono text-[9px]">↑↓</kbd>
            {commonText("keyboardNavigate")}</span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 rounded bg-muted/30 font-mono text-[9px]">↵</kbd>
            {commonText("keyboardSelect")}</span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 rounded bg-muted/30 font-mono text-[9px]">esc</kbd>
            {commonText("keyboardClose")}</span>
        </div>
      </div>
    );

    return (
      <>
        {renderTrigger()}
        {open && createPortal(renderPanel(), document.body)}
      </>
    );
  },
);

TagPicker.displayName = "TagPicker";
